import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertCircle, Info } from "lucide-react";
import {
  sessionsAPI,
  exercisesAPI,
  studentsAPI,
  studentSubscriptionsAPI,
  sessionExercisesAPI,
  sessionNotesAPI,
} from "../services/api";
import moment from "moment";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../App";
import { useApp } from "../contexts/AppContext";
import Skeleton from "../components/ui/Skeleton";
import { useToast } from "../contexts/ToastContext";
import { SessionStudentSection } from "../components/sessions/SessionStudentSection";
import { SessionTrainingSection } from "../components/sessions/SessionTrainingSection";
import { SessionNotesSection } from "../components/sessions/SessionNotesSection";
import { PreviousSessionsSection } from "../components/sessions/SessionPreviousSessionSection";
import StudentMeasurementModal from "../components/student-details/StudentMeasuremetModal";
import { SessionHeader } from "../components/sessions/SessionHeader";
import ExerciseModal from "../components/ui/ExerciseModal";
import SessionModal from "../components/sessions/SessionModal";
import { useSessionsModal } from "../hooks/useSessionsModal";
import ConfirmDialog from "../components/ui/ConfirmDialog";

// ── Group session status logic ──────────────────────────────────────────────
// A "group session" is rendered as N student rows, each backed by its own
// session record. The header badge at the top must reflect the group as a
// whole (not just the first row's status, which was the previous bug).
//
// Display rule (statuses → header badge):
//   • any 'scheduled'                 → 'scheduled'
//   • else any 'completed'            → 'completed'
//   • else all 'cancelled'            → 'cancelled'
//   • else all 'no-show'              → 'no-show'
//   • else mix of cancelled + no-show → 'no-show' (default tiebreak)
const aggregateGroupStatus = (statuses) => {
  const list = (statuses || []).filter(Boolean);
  if (list.length === 0) return 'scheduled';
  if (list.some((s) => s === 'scheduled')) return 'scheduled';
  if (list.some((s) => s === 'completed')) return 'completed';
  if (list.every((s) => s === 'cancelled')) return 'cancelled';
  if (list.every((s) => s === 'no-show')) return 'no-show';
  if (list.some((s) => s === 'no-show')) return 'no-show';
  return 'cancelled';
};

// Propagation rule (header click → per-student target). Returns null when
// the student row should be left untouched.
//   • header 'completed': only 'scheduled' rows flip; cancelled / no-show stay
//   • header 'cancelled': everyone flips to cancelled (incl. completed)
//   • header 'no-show'  : cancelled stays; everyone else → no-show
//   • header 'scheduled': only 'completed' rows revert; cancelled / no-show stay
const computeStudentTargetStatus = (currentStatus, headerStatus) => {
  switch (headerStatus) {
    case 'completed':
      return currentStatus === 'scheduled' ? 'completed' : null;
    case 'cancelled':
      return currentStatus === 'cancelled' ? null : 'cancelled';
    case 'no-show':
      if (currentStatus === 'cancelled' || currentStatus === 'no-show') return null;
      return 'no-show';
    case 'scheduled':
      return currentStatus === 'completed' ? 'scheduled' : null;
    default:
      return null;
  }
};

export const SessionDetailsPage = () => {
  const { colors } = useTheme();
  const { user } = useAuth();
  const toast = useToast();
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Reactive org id from context — a bare localStorage read here never
  // re-evaluates after login, which left deep-linked session pages without
  // data until a manual refresh (same race as the sessions calendar).
  const { selectedOrganizationId } = useApp();
  const organizationId = selectedOrganizationId || localStorage.getItem("selectedOrganizationId");
  const isLoadedRef = React.useRef(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [previousSessions, setPreviousSessions] = useState([]);
  const [expandedPreviousSessionId, setExpandedPreviousSessionId] =
    useState(null);
  const [sessionExercises, setSessionExercises] = useState([]);
  const [allExercises, setAllExercises] = useState([]);
  const [loadingExercises, setLoadingExercises] = useState(false);
  const [showMeasurementModal, setShowMeasurementModal] = useState(false);
  // Removed local measurementData state as it is handled by the modal now
  const [savingMeasurement, setSavingMeasurement] = useState(false);
  const [studentsData, setStudentsData] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [sessionNotes, setSessionNotes] = useState([]); // Array of note objects
  const [savingNotes, setSavingNotes] = useState(false);
  const [savingExercises, setSavingExercises] = useState(false);
  const [showExerciseModal, setShowExerciseModal] = useState(false);
  const [newExerciseName, setNewExerciseName] = useState("");
  const [pendingExerciseId, setPendingExerciseId] = useState(null);
  const [creatingExercise, setCreatingExercise] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Per-student removal (group sessions only). Holds the student object the
  // user clicked the trash icon on; null when no confirm dialog is open.
  const [removeStudentTarget, setRemoveStudentTarget] = useState(null);
  const [removingStudent, setRemovingStudent] = useState(false);


  // Get current user info from auth context
  const currentUserId = user?.username || user?.email || "unknown";
  const currentUserName = user?.name || user?.email || "Unknown User";

  // Accordion states
  const [expandedSections, setExpandedSections] = useState({
    student: true,
    training: false,
    notes: false,
    previousSessions: false,
  });

  // Navigation back to sessions
  const handleBack = () => {
    navigate("/sessions");
  };

  // Toggle accordion sections
  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Load session details and all related data in one go
  const loadSessionDetails = useCallback(async () => {
    if (!sessionId) return;

    setLoading(true);
    setError(null);

    try {
      console.log(`[Performance] Fetching full details for session ${sessionId}`);

      // 1. Fetch full session details (Participants, Lessons, Notes, History)
      const detailsPromise = sessionsAPI.getFullDetails(organizationId, sessionId);

      // 2. Fetch all exercises for the selection bank (if not already loaded)
      const exercisesPromise = allExercises.length === 0 ? exercisesAPI.getAll() : Promise.resolve(null);

      const [detailsRes, exercisesRes] = await Promise.all([detailsPromise, exercisesPromise]);
      const { data: detailsData } = detailsRes.data || {};

      if (!detailsData || !detailsData.session) {
        setError(t("sessionDetails.notFound"));
        setLoading(false);
        return;
      }

      // Map data to state
      setSessionData(detailsData.session);
      setStudentsData(detailsData.participants || []);
      setSessionExercises((detailsData.exercises || []).map(ex => ({ ...ex, isNew: false })));
      setSessionNotes(detailsData.notes || []);
      setPreviousSessions(detailsData.previousSessions || []);

      if (exercisesRes) {
        const exercisesList = (exercisesRes.data.data || []).map(ex => ({
          ...ex,
          id: ex.id || ex.SK,
        }));
        setAllExercises(exercisesList);
      }

      console.log(`[Performance] Load complete. Participants: ${detailsData.participants?.length}, Exercises: ${detailsData.exercises?.length}`);

    } catch (err) {
      console.error("Error loading full session details:", err);
      setError(t("sessionDetails.errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [sessionId, organizationId, allExercises.length, t]);

  // useSessionsModal hook for unified editing (initialized after loadSessionDetails)
  const {
    showModal,
    modalType,
    selectedEvent,
    formData,
    formErrors,
    submitting: submittingModal,
    formError: modalFormError,
    loadingSessionTypes,
    sessionTypes: modalSessionTypes,
    mainSessionTypes,
    subPackages,
    studentSubscriptions,
    loadingStudentSubscriptions,
    sessionTimes,
    openEditModal,
    openGroupManageModal,
    closeModal,
    handleSubmit: handleModalSubmit,
    setFormData,
    setSessionTimes,
    addSessionTime,
    removeSessionTime,
    updateSessionTime,
    handleStudentsChange,
    organizations,
    loadingOrganizations,
    isAllOrgsMode,
    loadSessionTypes,
    modalStudents,
    modalTrainers,
  } = useSessionsModal(organizationId, [], [], loadSessionDetails);

  // Update individual student session status (for group sessions)
  const updateIndividualSessionStatus = async (targetSessionId, orgId, newStatus) => {
    try {
      await sessionsAPI.updateStatus(orgId, targetSessionId, newStatus);

      // Update local state for students list
      setStudentsData(prev => prev.map(s =>
        s.sessionId === targetSessionId ? { ...s, currentStatus: newStatus } : s
      ));

      toast.success(t("sessionDetails.toasts.studentStatusUpdated", { status: newStatus }));
    } catch (error) {
      console.error("Error updating individual session status:", error);
      toast.error(t("sessionDetails.toasts.statusUpdateFailed"));
    }
  };

  // Update previous session status
  const updatePreviousSessionStatus = async (targetSessionId, newStatus) => {
    if (!sessionData?.organizationId) return;

    try {
      await sessionsAPI.updateStatus(
        sessionData.organizationId,
        targetSessionId,
        newStatus,
      );

      setPreviousSessions((prev) =>
        prev.map((s) =>
          s.id === targetSessionId ? { ...s, status: newStatus } : s,
        ),
      );

      // Also reload student details to refresh session credits/stats if needed
      loadSessionDetails();
    } catch (error) {
      console.error("Error updating previous session status:", error);
      toast.error(t("sessionDetails.toasts.sessionStatusUpdateFailed"));
    }
  };

  // Update session status (group-aware).
  // The header status button represents the *aggregate* of all student rows;
  // pressing it propagates to each row via computeStudentTargetStatus().
  const updateSessionStatus = async (newStatus) => {
    if (!sessionData?.organizationId) return;

    try {
      // Build a per-student update plan. Skip rows where the rule says
      // "leave untouched" so we never mutate cancelled/no-show by accident.
      const plans = (studentsData || [])
        .map((student) => {
          const targetId =
            student.sessionId ||
            (student.session && (student.session.id || student.session.SK));
          if (!targetId) return null;
          const currentStatus =
            student.currentStatus || student.session?.status || sessionData.status || 'scheduled';
          const targetStatus = computeStudentTargetStatus(currentStatus, newStatus);
          if (targetStatus === null) return null;
          return { targetId, targetStatus, studentId: student.id };
        })
        .filter(Boolean);

      if (plans.length === 0 && (!studentsData || studentsData.length <= 1)) {
        // Solo session fallback (no enriched studentsData yet) — write the
        // session row directly so the page still works for non-group flows.
        await sessionsAPI.update(sessionData.organizationId, sessionId, {
          status: newStatus,
        });
      } else if (plans.length > 0) {
        await Promise.all(
          plans.map((p) =>
            sessionsAPI.update(sessionData.organizationId, p.targetId, {
              status: p.targetStatus,
            }),
          ),
        );
      }

      // Optimistic local sync so the UI updates instantly without waiting
      // for the reload roundtrip.
      if (plans.length > 0) {
        setStudentsData((prev) =>
          prev.map((s) => {
            const planEntry = plans.find((p) => p.studentId === s.id);
            if (!planEntry) return s;
            return { ...s, currentStatus: planEntry.targetStatus };
          }),
        );
      }
      setSessionData((prev) => ({
        ...prev,
        status: newStatus, // refined by aggregate after reload
      }));

      // Reload to recalculate package details, kotas, finance entries, etc.
      loadSessionDetails();

      const isGroup = (studentsData?.length || 0) > 1;
      const skipped = (studentsData?.length || 0) - plans.length;
      const successMsg = isGroup
        ? t("sessionsPage.messages.groupStatusUpdated", {
            count: plans.length,
            status: newStatus,
          }) +
          (skipped > 0
            ? ' ' + t("sessionDetails.toasts.studentsSkippedSuffix", { count: skipped })
            : '')
        : t("sessionDetails.toasts.sessionMarkedAs", { status: newStatus });

      toast.success(successMsg);
    } catch (error) {
      console.error("Error updating session status:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        t("sessionDetails.toasts.sessionStatusUpdateFailed");
      toast.error(errorMessage);
    }
  };

  // handleMeasurementChange removed as it is handled by the modal

  const saveMeasurement = async (dataToSave) => {
    if (!selectedStudent || !selectedStudent.organizationId) {
      toast.error(t("sessionDetails.toasts.studentInfoMissing"));
      return;
    }

    setSavingMeasurement(true);
    try {
      await studentsAPI.addMeasurement(
        selectedStudent.organizationId,
        selectedStudent.id,
        dataToSave,
      );
      setShowMeasurementModal(false);
      toast.success(t("sessionDetails.toasts.measurementSaved"));

      // Reload student details to refresh the alert
      loadSessionDetails();
    } catch (error) {
      console.error("Error saving measurement:", error);
      toast.error(t("sessionDetails.toasts.measurementSaveFailed"));
    } finally {
      setSavingMeasurement(false);
    }
  };

  // Update session exercise
  const updateSessionExercise = (id, field, value) => {
    setSessionExercises((prev) =>
      prev.map((exercise) => {
        if (exercise.id === id) {
          const updated = { ...exercise, [field]: value };

          // If updating exerciseId, also update exerciseName for display
          if (field === "exerciseId") {
            const selectedEx = allExercises.find(
              (ex) => ex.id === value || ex.SK === value,
            );
            if (selectedEx) {
              updated.exerciseName = selectedEx.name;
            }
          }

          return updated;
        }
        return exercise;
      }),
    );
  };

  // Add new session exercise
  const addSessionExercise = (exerciseDetails = null) => {
    const newExercise = {
      id: `temp-${Date.now()}`, // Temp ID for new exercises
      exerciseId: exerciseDetails?.id || "",
      exerciseName: exerciseDetails?.name || "",
      weight: "",
      reps: "",
      sets: "",
      notes: "",
      completed: false,
      visible: true,
      isNew: true, // Flag to track new vs existing
    };
    setSessionExercises((prev) => [...prev, newExercise]);
  };

  // Remove session exercise
  const removeSessionExercise = async (id) => {
    const exercise = sessionExercises.find((ex) => ex.id === id);

    // If it's an existing exercise from DB, delete it from backend
    // Use sessionData.organizationId for actual org ID (not "all")
    if (
      exercise &&
      !exercise.isNew &&
      exercise.id &&
      sessionData?.organizationId
    ) {
      try {
        await sessionExercisesAPI.remove(
          sessionData.organizationId,
          sessionId,
          exercise.id,
        );
      } catch (error) {
        console.error("Error removing exercise:", error);
        setError(t("sessionDetails.errors.removeExerciseFailed"));
        return;
      }
    }

    setSessionExercises((prev) => prev.filter((ex) => ex.id !== id));
  };

  // Save session exercises
  const saveSessionExercises = async () => {
    if (!sessionData?.organizationId) {
      setError(t("sessionDetails.errors.orgNotFound"));
      return;
    }

    const actualOrgId = sessionData.organizationId;
    setSavingExercises(true);
    try {
      for (const exercise of sessionExercises) {
        if (exercise.exerciseId) {
          if (exercise.isNew) {
            // Add new exercise
            const response = await sessionExercisesAPI.add(
              actualOrgId,
              sessionId,
              exercise.exerciseId,
              {
                sets: parseInt(exercise.sets) || 0,
                reps: parseInt(exercise.reps) || 0,
                weight: parseFloat(exercise.weight) || 0,
                notes: exercise.notes || "",
                completed: exercise.completed || false,
              },
            );
            // Update local state - mark as not new anymore
            if (response?.data?.data?.id) {
              setSessionExercises((prev) =>
                prev.map((ex) =>
                  ex.id === exercise.id
                    ? { ...response.data.data, isNew: false }
                    : ex,
                ),
              );
            }
          } else {
            // Update existing exercise
            await sessionExercisesAPI.update(
              actualOrgId,
              sessionId,
              exercise.id,
              {
                sets: parseInt(exercise.sets) || 0,
                reps: parseInt(exercise.reps) || 0,
                weight: parseFloat(exercise.weight) || 0,
                notes: exercise.notes || "",
                completed: exercise.completed || false,
              },
            );
          }
        }
      }
      // Reload to get fresh data
      const sessionExResponse = await sessionExercisesAPI.getBySession(
        actualOrgId,
        sessionId,
      );
      const existingExercises = sessionExResponse?.data?.data || [];
      setSessionExercises(
        existingExercises.map((ex) => ({ ...ex, isNew: false })),
      );
      console.log("Session exercises saved successfully");
    } catch (error) {
      console.error("Error saving session exercises:", error);
      setError(t("sessionDetails.errors.saveExercisesFailed"));
    } finally {
      setSavingExercises(false);
    }
  };

  // Open modal to create new exercise
  const openNewExerciseModal = (name, pendingId = null) => {
    setNewExerciseName(name);
    setPendingExerciseId(pendingId);
    setShowExerciseModal(true);
  };

  // Create new exercise and add to session
  const handleCreateExercise = async (exerciseData) => {
    setCreatingExercise(true);
    try {
      // 1. Create exercise in global library
      const response = await exercisesAPI.create(
        sessionData.organizationId,
        exerciseData,
      );
      const newEx = response.data.data;

      if (newEx) {
        // 2. Add to allExercises list so it's searchable/available
        const normalizedEx = { ...newEx, id: newEx.id || newEx.SK };
        setAllExercises((prev) => [...prev, normalizedEx]);

        // 3. Add to currently active session
        if (pendingExerciseId) {
          // If we were selecting for a specific card, update that card using the new ID/SK
          updateSessionExercise(
            pendingExerciseId,
            "exerciseId",
            normalizedEx.id,
          );
        } else {
          // Otherwise add as a new card
          addSessionExercise(normalizedEx);
        }

        // 4. Close modal
        setShowExerciseModal(false);
        setNewExerciseName("");
        setPendingExerciseId(null);
      }
    } catch (err) {
      console.error("Error creating new exercise:", err);
      setError(t("sessionDetails.errors.createExerciseFailed"));
    } finally {
      setCreatingExercise(false);
    }
  };

  // Add new session note
  const addSessionNote = async (content) => {
    if (!sessionData?.organizationId) {
      setError(t("sessionDetails.errors.orgNotFound"));
      return;
    }

    setSavingNotes(true);
    try {
      const response = await sessionNotesAPI.add(
        sessionData.organizationId,
        sessionId,
        {
          content,
          authorId: currentUserId,
          authorName: currentUserName,
        },
      );
      if (response?.data?.data) {
        setSessionNotes((prev) => [response.data.data, ...prev]); // Add to beginning (newest first)
      }
      console.log("Session note added successfully");
    } catch (error) {
      console.error("Error adding session note:", error);
      setError(t("sessionDetails.errors.addNoteFailed"));
    } finally {
      setSavingNotes(false);
    }
  };

  // Update session note
  const updateSessionNote = async (noteId, content) => {
    if (!sessionData?.organizationId) {
      setError(t("sessionDetails.errors.orgNotFound"));
      return;
    }

    setSavingNotes(true);
    try {
      const response = await sessionNotesAPI.update(
        sessionData.organizationId,
        sessionId,
        noteId,
        {
          content,
          requesterId: currentUserId,
        },
      );
      if (response?.data?.data) {
        setSessionNotes((prev) =>
          prev.map((note) => (note.id === noteId ? response.data.data : note)),
        );
      }
      console.log("Session note updated successfully");
    } catch (error) {
      console.error("Error updating session note:", error);
      setError(t("sessionDetails.errors.updateNoteFailed"));
    } finally {
      setSavingNotes(false);
    }
  };

  // Delete session note
  const deleteSessionNote = async (noteId) => {
    if (!sessionData?.organizationId) {
      setError(t("sessionDetails.errors.orgNotFound"));
      return;
    }

    setSavingNotes(true);
    try {
      await sessionNotesAPI.remove(
        sessionData.organizationId,
        sessionId,
        noteId,
        currentUserId,
      );
      setSessionNotes((prev) => prev.filter((note) => note.id !== noteId));
      console.log("Session note deleted successfully");
    } catch (error) {
      console.error("Error deleting session note:", error);
      setError(t("sessionDetails.errors.deleteNoteFailed"));
    } finally {
      setSavingNotes(false);
    }
  };

  const deleteSession = async () => {
    if (!sessionData?.organizationId) return;

    try {
      const deleteResponse = await sessionsAPI.delete(
        sessionData.organizationId,
        sessionId,
      );
      if (deleteResponse.status === 200) {
        navigate("/sessions");
      }
    } catch (error) {
      console.error(error);
    }
  };

  // Confirm + execute per-student removal in a group session.
  // Each student in a group session has its own session row in DynamoDB,
  // so deleting that single row removes them and refunds their package
  // quota (handled by SessionService.deleteSession on the backend).
  // If we just removed the last student the whole session is gone, so we
  // bounce back to /sessions instead of trying to reload an empty page.
  const handleConfirmRemoveStudent = async () => {
    if (!removeStudentTarget || removingStudent || !sessionData?.organizationId) return;

    const targetSessionId =
      removeStudentTarget.sessionId ||
      removeStudentTarget.session?.id ||
      removeStudentTarget.session?.SK;
    if (!targetSessionId) {
      toast.error(t("sessionDetails.removeStudent.failed"));
      setRemoveStudentTarget(null);
      return;
    }

    const wasLastStudent = (studentsData?.length || 0) <= 1;
    setRemovingStudent(true);
    try {
      await sessionsAPI.delete(sessionData.organizationId, targetSessionId);

      toast.success(t("sessionDetails.removeStudent.success"));
      setRemoveStudentTarget(null);

      if (wasLastStudent) {
        navigate("/sessions");
        return;
      }

      // Optimistic local state — drop the row immediately so the UI doesn't
      // show a flash of the removed student before reload.
      setStudentsData((prev) =>
        prev.filter(
          (s) =>
            (s.sessionId || s.session?.id || s.session?.SK) !== targetSessionId,
        ),
      );
      loadSessionDetails();
    } catch (error) {
      console.error("Error removing student from session:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        t("sessionDetails.removeStudent.failed");
      toast.error(errorMessage);
    } finally {
      setRemovingStudent(false);
    }
  };

  // Open edit modal with session data pre-filled
  const handleOpenEditModal = async () => {
    if (!sessionData) return;

    // Use group management flow even for single sessions to allow adding/removing students
    // (matches the "Create Session" behavior requested by the user)
    const groupEvent = {
      id: sessionData.id,
      trainerId: sessionData.trainerId,
      start: sessionData.startTime,
      end: sessionData.endTime,
      organizationId: sessionData.organizationId,
      status: sessionData.status,
      students: studentsData.map(s => ({
        studentId: s.id,
        session: {
          id: s.sessionId,
          sessionTypeId: String(sessionData.sessionTypeId || ""),
          membershipPackageId: String(sessionData.membershipPackageId || sessionData.sessionTypeId || ""),
          packageName: sessionData.sessionTypeName,
          status: s.currentStatus
        }
      }))
    };
    openGroupManageModal(groupEvent);
  };

  const handleModalSuccess = (message) => {
    toast.success(message || t("sessionsPage.messages.updated"));
    closeModal();
    loadSessionDetails();
  };

  // Handle toggle previous session expansion
  const handleTogglePreviousSession = async (sessionId) => {
    if (expandedPreviousSessionId === sessionId) {
      setExpandedPreviousSessionId(null);
      return;
    }

    setExpandedPreviousSessionId(sessionId);

    const session = previousSessions.find((s) => s.id === sessionId);
    if (session && !session.exercises) {
      try {
        const response = await sessionExercisesAPI.getBySession(
          sessionData.organizationId,
          sessionId,
        );
        const exercises = response.data.data || [];

        setPreviousSessions((prev) =>
          prev.map((s) => (s.id === sessionId ? { ...s, exercises } : s)),
        );
      } catch (error) {
        console.error("Error fetching previous session exercises:", error);
      }
    }
  };

  useEffect(() => {
    // Wait until the organization resolves; keying the guard on org+session
    // lets the load re-run when the org arrives after mount (fresh login),
    // instead of being blocked by the first org-less attempt.
    if (!organizationId) return;
    const loadKey = `${organizationId}:${sessionId}`;
    if (isLoadedRef.current === loadKey) return;
    isLoadedRef.current = loadKey;
    loadSessionDetails();
  }, [loadSessionDetails, sessionId, organizationId]);

  // Loading state (Skeleton Loader)
  if (loading) {
    return (
      <div className={`min-h-screen ${colors.bgSecondary} p-4 md:p-8 space-y-8`}>
        {/* Header Skeleton */}
        <div className={`flex flex-col md:flex-row md:items-center justify-between gap-6 p-6 rounded-2xl ${colors.bgPrimary} border ${colors.tableBorder} shadow-xl`}>
          <div className="flex items-center gap-4">
            <Skeleton variant="rectangle" width="40px" height="10" />
            <div className="space-y-2">
              <Skeleton width="180px" height="6" />
              <Skeleton width="120px" height="4" className="opacity-60" />
            </div>
          </div>
          <div className="flex gap-3">
            <Skeleton width="40px" height="10" />
            <Skeleton width="40px" height="10" />
            <Skeleton width="120px" height="10" />
          </div>
        </div>

        {/* Content Skeleton Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className={`lg:col-span-2 space-y-6`}>
            {/* Participants Section Skeleton */}
            <div className={`p-6 rounded-2xl ${colors.bgPrimary} border ${colors.tableBorder} shadow-xl space-y-6`}>
              <div className="flex items-center justify-between mb-2">
                <Skeleton width="160px" height="6" />
                <Skeleton width="120px" height="10" />
              </div>
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className={`p-4 rounded-xl border ${colors.tableBorder} ${colors.inputBg} opacity-40 flex items-center gap-4`}>
                    <Skeleton variant="circle" width="48px" height="12" className="bg-white/20" />
                    <div className="flex-1 space-y-2">
                      <Skeleton width="30%" height="4" className="opacity-50" />
                      <Skeleton width="45%" height="3" className="opacity-30" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {/* Info Cards Skeletons */}
            {[1, 2, 3].map(i => (
              <div key={i} className={`p-6 rounded-2xl ${colors.bgPrimary} border ${colors.tableBorder} shadow-xl space-y-3`}>
                <Skeleton width="40%" height="4" className="opacity-60" />
                <Skeleton width="100%" height="8" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div
        className={`flex flex-col items-center justify-center h-screen ${colors.bgPrimary}`}
      >
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p className={`${colors.textSecondary} text-center mb-2`}>{error}</p>
        <button
          onClick={handleBack}
          className={`mt-4 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-semibold shadow-md hover:shadow-lg transform hover:scale-[1.02]`}
        >
          {t("sessionDetails.backToSessions")}
        </button>
      </div>
    );
  }

  // No session data
  if (!sessionData) {
    return (
      <div
        className={`flex flex-col items-center justify-center h-screen ${colors.bgPrimary}`}
      >
        <Info className={`w-12 h-12 ${colors.textSecondary} mb-4`} />
        <p className={`${colors.textSecondary}`}>{t("sessionDetails.notFound")}</p>
        <button
          onClick={handleBack}
          className={`mt-4 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-semibold shadow-md hover:shadow-lg transform hover:scale-[1.02]`}
        >
          {t("sessionDetails.backToSessions")}
        </button>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${colors.bgPrimary}`}>
      {/* Session Header Component.
          For groups we override sessionData.status with the aggregate of all
          student rows so the badge no longer mirrors only the first row. */}
      <SessionHeader
        sessionData={
          studentsData && studentsData.length > 1
            ? {
                ...sessionData,
                status: aggregateGroupStatus(
                  studentsData.map(
                    (s) => s.currentStatus || s.session?.status,
                  ),
                ),
              }
            : sessionData
        }
        onBack={handleBack}
        onStatusChange={updateSessionStatus}
        onDelete={() => setShowDeleteConfirm(true)}
        onEdit={handleOpenEditModal}
      />

      <div className="px-3 sm:px-6 py-4 sm:py-6">
        {/* Student Section Component */}
        <SessionStudentSection
          expanded={expandedSections.student}
          onToggle={() => toggleSection("student")}
          studentsData={studentsData}
          loadingStudents={loadingStudents}
          onNavigateToStudent={(id) => navigate(`/students/${id}`)}
          onUpdateStatus={updateIndividualSessionStatus}
          onAddMeasurement={(student) => {
            setSelectedStudent(student);
            setShowMeasurementModal(true);
          }}
          onRemoveStudent={(student) => setRemoveStudentTarget(student)}
        />

        {/* Training Section Component */}
        <SessionTrainingSection
          expanded={expandedSections.training}
          onToggle={() => toggleSection("training")}
          sessionExercises={sessionExercises}
          allExercises={allExercises}
          onUpdateExercise={updateSessionExercise}
          onAddExercise={addSessionExercise}
          onRemoveExercise={removeSessionExercise}
          onAddNewExercise={openNewExerciseModal}
          onSave={saveSessionExercises}
          saving={savingExercises}
        />

        {/* Notes Section Component */}
        <SessionNotesSection
          expanded={expandedSections.notes}
          onToggle={() => toggleSection("notes")}
          notes={sessionNotes}
          onAddNote={addSessionNote}
          onUpdateNote={updateSessionNote}
          onDeleteNote={deleteSessionNote}
          saving={savingNotes}
          currentUserId={currentUserId}
          currentUserName={currentUserName}
        />

        {/* Previous Sessions Component */}
        <PreviousSessionsSection
          expanded={expandedSections.previousSessions}
          onToggle={() => toggleSection("previousSessions")}
          previousSessions={previousSessions}
          onUpdateStatus={updatePreviousSessionStatus}
          expandedSessionId={expandedPreviousSessionId}
          onSessionToggle={handleTogglePreviousSession}
        />
      </div>

      {/* Measurement Modal Component */}
      {/* Measurement Modal Component */}
      <StudentMeasurementModal
        isOpen={showMeasurementModal}
        onClose={() => setShowMeasurementModal(false)}
        onSave={saveMeasurement}
        saving={savingMeasurement}
        student={selectedStudent}
        editingMeasurement={null} // Always adding new
      />

      {/* New Exercise Modal */}
      <ExerciseModal
        isOpen={showExerciseModal}
        onClose={() => setShowExerciseModal(false)}
        onSubmit={handleCreateExercise}
        loading={creatingExercise}
        exercise={{ name: newExerciseName }}
        title={t("sessionDetails.exerciseModal.createTitle")}
      />

      {/* Unified Session Modal */}
      <SessionModal
        isOpen={showModal}
        modalType={modalType}
        selectedEvent={selectedEvent}
        formData={formData}
        formErrors={formErrors}
        formError={modalFormError}
        submitting={submittingModal}
        trainers={modalTrainers}
        students={modalStudents}
        sessionTypes={modalSessionTypes}
        mainSessionTypes={mainSessionTypes}
        subPackages={subPackages}
        studentSubscriptions={studentSubscriptions}
        loadingStudentSubscriptions={loadingStudentSubscriptions}
        loadingSessionTypes={loadingSessionTypes}
        sessionTimes={sessionTimes}
        organizationId={organizationId}
        onClose={closeModal}
        onSubmit={(data) => handleModalSubmit(data, handleModalSuccess)}
        onFormDataChange={setFormData}
        onSessionTimesChange={setSessionTimes}
        onAddSessionTime={addSessionTime}
        onRemoveSessionTime={removeSessionTime}
        onUpdateSessionTime={updateSessionTime}
        handleStudentsChange={handleStudentsChange}
        organizations={organizations}
        loadingOrganizations={loadingOrganizations}
        isAllOrgsMode={isAllOrgsMode}
        onLoadSessionTypes={loadSessionTypes}
      />
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={async () => {
          setShowDeleteConfirm(false);
          await deleteSession();
        }}
        title={t("sessionDetails.deleteSessionConfirmTitle")}
        message={t("sessionDetails.deleteSessionConfirmMessage")}
        confirmText={t("common.delete")}
        cancelText={t("sessionDetails.notes.cancel")}
        confirmButtonClass="bg-red-600 hover:bg-red-700 text-white"
      />

      {/* Per-student remove confirm (group sessions only). The wording shifts
          when only one student is left because removing them deletes the
          entire session — we want the user to know that explicitly. */}
      <ConfirmDialog
        isOpen={!!removeStudentTarget}
        onClose={() => !removingStudent && setRemoveStudentTarget(null)}
        onConfirm={handleConfirmRemoveStudent}
        title={t("sessionDetails.removeStudent.confirmTitle")}
        message={t(
          (studentsData?.length || 0) <= 1
            ? "sessionDetails.removeStudent.confirmMessageLast"
            : "sessionDetails.removeStudent.confirmMessage",
          {
            name: removeStudentTarget
              ? `${removeStudentTarget.name || ""} ${removeStudentTarget.surname || ""}`.trim() ||
                t("sessionDetails.notes.unknown")
              : "",
          },
        )}
        confirmText={t("sessionDetails.removeStudent.confirmButton")}
        cancelText={t("sessionDetails.notes.cancel")}
        confirmButtonClass="bg-rose-600 hover:bg-rose-700 text-white"
      />
    </div>
  );
};
